import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import {
  FloatingFocusManager,
  FloatingPortal,
  useMergeRefs,
} from '@floating-ui/react';
import {
  Button,
  DropdownCore,
  DropdownItem,
  TDropdownItem,
  TDropdownSection,
  TooltipCore,
  useFloatingTooltip,
} from '@payaca/ambrosia';

import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import useDropdownMenu from './useDropdownMenu';

export type TProps = {
  id: UniqueIdentifier;
  items: TDropdownItem[] | TDropdownSection[];
};

const PipelineStageConfigDragHandle: FC<TProps> = (props) => {
  const { id, items } = props;

  const { attributes, listeners, setActivatorNodeRef, isDragging } =
    useSortable({ id });

  const dropdown = useDropdownMenu({ enabled: !isDragging });
  const tooltip = useFloatingTooltip({
    enabled: !dropdown.isOpen && !isDragging,
  });

  const mergedRef = useMergeRefs([
    setActivatorNodeRef,
    dropdown.refs.setReference,
    tooltip.refs.setReference,
  ]);

  return (
    <>
      <Button
        ref={mergedRef}
        className="px-0"
        variant="ghost"
        size="sm"
        colour="black"
        aria-label="Stage options"
        {...tooltip.getReferenceProps(
          dropdown.getReferenceProps({
            ...attributes,
            ...listeners,
          })
        )}
      >
        <UntitledIcon name="draggable" />
      </Button>

      {tooltip.isOpen && (
        <FloatingPortal>
          <TooltipCore
            ref={tooltip.refs.setFloating}
            style={tooltip.floatingStyles}
            {...tooltip.getFloatingProps()}
          >
            <div>
              <b>Drag</b> to reorder stage
            </div>
            <div>
              <b>Click</b> for more options
            </div>
          </TooltipCore>
        </FloatingPortal>
      )}

      {dropdown.isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={dropdown.context} modal={false}>
            <DropdownCore
              ref={dropdown.refs.setFloating}
              style={dropdown.floatingStyles}
              {...dropdown.getFloatingProps()}
              items={items}
              renderItem={(item, index) => (
                <DropdownItem
                  key={index}
                  ref={(node) => {
                    dropdown.listRef.current[index] = node;
                  }}
                  role="menuitem"
                  item={item}
                  tabIndex={dropdown.activeIndex === index ? 0 : -1}
                  data-active={dropdown.activeIndex === index ? '' : undefined}
                  {...dropdown.getItemProps({
                    onClick: (event: any) => {
                      if (item.disabled) return;
                      item.onClick?.(event);
                      dropdown.setIsOpen(false);
                    },
                  })}
                />
              )}
            />
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

export default PipelineStageConfigDragHandle;
